import { useEffect, useState } from "react";
import {
  FiArrowRight,
  FiCheck,
  FiX,
  FiTruck,
  FiPlus,
  FiPackage,
  FiCreditCard,
  FiClock,
  FiAlertCircle,
} from "react-icons/fi";
import { TbLoader2 } from "react-icons/tb";
import axios from "../../../Utils/axiosInstance";
import AppLoader from "../../Common/AppLoader";
import SampleBankTransferUploadBanner from "./SampleBankTransferUploadBanner";
import SampleBankTransferStatusCard from "./SampleBankTransferStatusCard";


const SelectedQuotationsListView = ({
  quotations,
  order,
  onViewManage,
  onAddMore,
  onProceedFullOrder,
  onProceedSamplePayment,
  updating,
  fullOrderLoadingId,
  sampleCart, hideAddMore, refreshQuotations
}) => {
  const [localQuotations, setLocalQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sampleBankTransferDocs, setSampleBankTransferDocs] = useState({});

  useEffect(() => {
    if (quotations && quotations.length > 0) {
      setLocalQuotations(quotations);
    }
  }, [quotations]);

  useEffect(() => {
    const currentList = quotations && quotations.length > 0 ? quotations : localQuotations;
    const needed = currentList.filter(
      (q) =>
        q?.paymentDetails?.sample?.method === "Direct_Bank_Transfer" &&
        !!q?.invoice_details?.sample?.proforma_invoice
    );
    if (needed.length === 0) return;

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        needed.map(async (q) => {
          try {
            const { data } = await axios.post("/bank-transfer/get-by-quotation", {
              selectedQuotationId: q._id,
              type: "Sample",
            });
            return [q._id, data?.success ? data.data : null];
          } catch {
            return [q._id, null];
          }
        })
      );
      if (!cancelled) {
        setSampleBankTransferDocs((prev) => ({
          ...prev,
          ...Object.fromEntries(entries),
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [quotations, localQuotations]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getTransportStatus = (quotation) => {
    const transport = quotation?.transportDetails?.find(
      (t) => t?.charge_is_for === "Sample"
    );
    if (!transport) return { status: "not_set", label: "Not set", amount: null };
    if (transport.transportation_charges === "Available") {
      return { status: "ready", label: "Ready", amount: transport.transportationAmount };
    }
    return { status: "waiting", label: "Pending", amount: null };
  };

  const getSampleApproval = (quotation) => {
    const approval = quotation?.sampleDetails?.approval;
    if (approval === "Approved") return { color: "emerald", label: "Approved", icon: "✓" };
    if (approval === "Rejected") return { color: "red", label: "Rejected", icon: "✕" };
    return { color: "amber", label: "Pending", icon: "⏳" };
  };

  const formatPrice = (num) => {
    const n = Number(num || 0);
    if (isNaN(n)) return "0";
    return n % 1 !== 0
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : n.toLocaleString("en-IN");
  };

  const isQuotationPaid = (quotationId) => {
    if (!sampleCart?.items) return false;
    const item = sampleCart.items.find(
      (item) => item.quotationId?._id?.toString() === quotationId.toString()
    );
    return item?.isPaid || false;
  };

  // ── Get price breakdown for card display ─────────────────────────────────
  const getPriceBreakdown = (quotation) => {
    const isFullOrder =
      quotation?.buyerDecision === "Proceed_Full_Order" &&
      quotation?.sampleDetails?.requested === true;

    if (isFullOrder) {
      const fullTransport = quotation?.transportDetails?.find(
        (t) => t?.charge_is_for === "Full_Order"
      );
      const bd = fullTransport?.breakdown?.breakdown;
      if (bd) return { type: "full", data: bd, available: true };
    }

    // Sample breakdown
    const sampleTransport = quotation?.transportDetails?.find(
      (t) => t?.charge_is_for === "Sample"
    );
    const bd = sampleTransport?.breakdown?.breakdown;
    if (bd) return { type: "sample", data: bd, available: true };

    return { available: false };
  };

  // ── Get ALL price breakdowns ─────────────────────────────────────────────
  const getAllBreakdowns = (quotation) => {
    const result = [];

    const sampleTransport = quotation?.transportDetails?.find(
      (t) => t?.charge_is_for === "Sample"
    );
    if (sampleTransport?.breakdown?.breakdown) {
      result.push({ type: "sample", data: sampleTransport.breakdown.breakdown });
    }

    const fullTransport = quotation?.transportDetails?.find(
      (t) => t?.charge_is_for === "Full_Order"
    );
    if (fullTransport?.breakdown?.breakdown) {
      result.push({ type: "full", data: fullTransport.breakdown.breakdown });
    }

    return result;
  };

  // ── Contextual status message ─────────────────────────────────────────────
  const getStatusMessage = (quotation) => {
    const isPaid = isQuotationPaid(quotation._id);
    const sampleTransport = quotation?.transportDetails?.find(
      (t) => t?.charge_is_for === "Sample"
    );
    const isTransportReady = sampleTransport?.transportation_charges === "Available";
    const sampleInvoiceConfirmed = !!quotation?.invoice_details?.sample?.proforma_invoice;
    const fullOrderInvoiceConfirmed = !!quotation?.invoice_details?.full_order?.proforma_invoice;
    const sampleApproval = quotation?.sampleDetails?.approval;
    const buyerDecision = quotation?.buyerDecision;

    const isSampleBankTransfer =
      quotation?.paymentDetails?.sample?.method === "Direct_Bank_Transfer";
    const sampleBankTransferStatus =
      quotation?.paymentDetails?.sample?.BankTransfer_Status;
    const sampleBankTransferVerified =
      sampleBankTransferStatus === "VERIFIED";
    const sampleBankTransferUploaded =
      sampleBankTransferStatus === "UPLOADED";

    const hasDispatchedSample = Array.isArray(quotation?.dispatchDetails) &&
      quotation.dispatchDetails.some((d) => d.type === "Sample" && d.status === "Dispatched");
    const hasDeliveredSample = Array.isArray(quotation?.dispatchDetails) &&
      quotation.dispatchDetails.some((d) => d.type === "Sample" && d.status === "Delivered");
    const hasDispatchedFull = Array.isArray(quotation?.dispatchDetails) &&
      quotation.dispatchDetails.some((d) => d.type === "Full_Order" && d.status === "Dispatched");
    const hasDeliveredFull = Array.isArray(quotation?.dispatchDetails) &&
      quotation.dispatchDetails.some((d) => d.type === "Full_Order" && d.status === "Delivered");

    // ── Full order path (after sample approval + proceeded) ──
    if (buyerDecision === "Proceed_Full_Order" && sampleApproval === "Approved") {
      if (hasDeliveredFull && !quotation?.fullOrderDetails?.approval) {
        return {
          type: "action", icon: "📦", color: "blue",
          text: "Full order delivered! Please confirm satisfaction or raise a dispute from the detail view.",
        };
      }
      if (hasDispatchedFull) {
        return {
          type: "info", icon: "🚚", color: "blue",
          text: "Your full order is on its way.",
        };
      }
      if (fullOrderInvoiceConfirmed) {
        return {
          type: "success", icon: "✅", color: "green",
          text: "Full order payment confirmed. Your order is being prepared for dispatch.",
        };
      }
      const fullTransport = quotation?.transportDetails?.find(
        (t) => t?.charge_is_for === "Full_Order"
      );
      const fullTransportReady = fullTransport?.transportation_charges === "Available";
      if (!fullTransportReady) {
        return {
          type: "waiting", icon: "⏳", color: "amber",
          text: "Full order initiated. Admin is calculating freight charges — you'll be notified when ready to pay.",
        };
      }
      return {
        type: "action", icon: "💳", color: "primary",
        text: "Freight charges confirmed. Open the detail view to complete your full order payment.",
      };
    }

    // ── Sample path ──
    if (sampleApproval === "Approved" && buyerDecision !== "Proceed_Full_Order") {
      return {
        type: "action", icon: "🚀", color: "emerald",
        text: "Sample approved! Tap 'View & Manage' to proceed to full order.",
      };
    }

    if (sampleApproval === "Rejected") return null;

    // Sample paid
    if (isPaid || sampleInvoiceConfirmed) {
      if (hasDeliveredSample && sampleApproval === "Pending") {
        return {
          type: "action", icon: "📦", color: "blue",
          text: "Sample delivered — Click on 'View & Manage Quotation' to approve or reject the sample.",
        };
      }
      if (hasDispatchedSample) {
        return {
          type: "info", icon: "🚚", color: "blue",
          text: "Your sample is on its way. You can track it with the tracking link. Once it's delivered mark it as received and approve or reject the sample in the detail view.",
        };
      }

      // ── Bank transfer: only VERIFIED counts as confirmed ──
      if (isSampleBankTransfer && sampleBankTransferStatus !== "VERIFIED") {
        if (sampleBankTransferStatus === "UPLOADED") {
          return {
            type: "info", icon: "📋", color: "blue",
            text: "Payment proof submitted. Your sample will be processed once our team verifies the transfer.",
          };
        }
        if (sampleBankTransferStatus === "REJECTED") {
          return {
            type: "action", icon: "❌", color: "amber",
            text: "Your bank transfer proof was rejected. Please re-upload it to proceed.",
          };
        }
        // No status yet / PENDING
        return {
          type: "waiting", icon: "🏦", color: "amber",
          text: "Order reserved via bank transfer. Upload your transfer proof — your sample will be processed once payment is verified.",
        };
      }

      return {
        type: "success", icon: "✅", color: "green",
        text: "Payment confirmed. Your sample is being prepared for dispatch.",
      };
    }

    // Not yet paid
    if (!isTransportReady) {
      return {
        type: "waiting", icon: "⏳", color: "amber",
        text: "Admin is confirming transport charges. You'll be able to pay once it's set.",
      };
    }

    return {
      type: "action", icon: "💳", color: "primary",
      text: "Transport charges confirmed. Use the 'Proceed to Sample Payment' button above to pay.",
    };
  };

  const displayQuotations = quotations && quotations.length > 0 ? quotations : localQuotations;

  const unpaidQuotations = displayQuotations.filter((q) => !isQuotationPaid(q._id));

  const allUnpaidTransportReady =
    unpaidQuotations.length > 0 &&
    unpaidQuotations.every((q) => {
      const t = q?.transportDetails?.find((t) => t?.charge_is_for === "Sample");
      return t?.transportation_charges === "Available";
    });

  const anyUnpaidInvoiceConfirmed = unpaidQuotations.some(
    (q) => !!q?.invoice_details?.sample?.proforma_invoice
  );

  if (loading) {
    return (
      <div className="h-[50vh] flex items-center justify-center">
        <AppLoader message="Loading Selected Quotations..." />
      </div>
    );
  }

  if (displayQuotations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-primaryColor/10 flex items-center justify-center mb-4">
          <FiPackage className="text-primaryColor text-2xl" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">No sample quotations yet</h3>
        <p className="text-sm text-gray-500 mb-5">Add quotations to your sample cart and proceed to see them here.</p>
        <button onClick={onAddMore} className="flex items-center gap-2 px-5 py-2.5 bg-primaryColor text-white rounded-xl text-sm font-bold hover:bg-primaryBtnHoverColor transition-colors">
          <FiPlus /> Browse Quotations
        </button>
      </div>
    );
  }

  const paidQuotations = displayQuotations.filter((q) => isQuotationPaid(q._id));
  // Quotations paid via bank transfer that still need proof uploaded/verified
  // ✅ Only show upload banner for quotations that NEED it (status is PENDING or not uploaded yet)
  const needsBankTransferProof = displayQuotations.filter((q) => {
    const method = q?.paymentDetails?.sample?.method;
    const status = q?.paymentDetails?.sample?.BankTransfer_Status;
    // Show banner only if: bank transfer method AND (no status yet OR status is PENDING)
    return method === "Direct_Bank_Transfer" && (!status || status === "PENDING");
  });

  return (
    <div className="space-y-4 pb-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            Sample Quotations
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primaryColor/10 text-primaryColor text-xs font-bold border border-primaryColor/20">
              {displayQuotations.length}
            </span>
            {paidQuotations.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold border border-green-200">
                {paidQuotations.length} Paid
              </span>
            )}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {unpaidQuotations.length > 0
              ? `${unpaidQuotations.length} quotation${unpaidQuotations.length !== 1 ? "s" : ""} pending payment`
              : "All quotations have been paid for"}
          </p>
        </div>

        {(order?.stages === "Sampling" || order?.stages === "Quotation") && !hideAddMore && order?.stages !== "Order-Fullfillment" && (
          <button onClick={onAddMore} className="flex items-center gap-1.5 px-3 py-2 border border-primaryColor/30 text-primaryColor rounded-xl text-xs font-bold hover:bg-primaryColor/5 transition-colors">
            <FiPlus className="text-xs" /> Add More
          </button>
        )}
      </div>

      {/* ── Proceed to Sample Payment Banner ── */}
      {allUnpaidTransportReady && !anyUnpaidInvoiceConfirmed && unpaidQuotations.length > 0 && (
        <div className="bg-gradient-to-r from-[#772037]/5 to-[#CC234F]/5 border border-[#772037]/20 rounded-2xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                All transport charges confirmed!
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {unpaidQuotations.length} quotation{unpaidQuotations.length !== 1 ? "s" : ""} ready for payment
              </p>
            </div>
            <button
              onClick={() => { if (typeof onProceedSamplePayment === "function") onProceedSamplePayment(unpaidQuotations); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#772037] to-[#CC234F] text-white rounded-xl text-sm font-bold hover:opacity-90 transition-all shadow-md shadow-[#772037]/20 flex-shrink-0"
            >
              <FiCreditCard className="text-sm" /> Proceed to Sample Payment
            </button>
          </div>
        </div>
      )}

      {/* ── Paid banner ── */}
      {paidQuotations.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center gap-2">
            <FiCheck className="text-green-600" />
            <p className="text-sm font-bold text-green-700">
              {paidQuotations.length} quotation{paidQuotations.length !== 1 ? "s" : ""} payment confirmed.
            </p>
          </div>
        </div>
      )}

      {/* ── NEW: Bulk bank transfer proof banner — shown ONCE ── */}
      {needsBankTransferProof.length > 0 && (
        <SampleBankTransferUploadBanner
          quotations={needsBankTransferProof}
          order={order}
          onDone={refreshQuotations}
        />
      )}

      {/* ── Waiting for transport banner ── */}
      {!allUnpaidTransportReady && !anyUnpaidInvoiceConfirmed && unpaidQuotations.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-center gap-2">
          <FiTruck className="text-amber-600 flex-shrink-0" />
          <p className="text-xs font-semibold text-amber-700">
            Waiting for admin to confirm transport charges on all unpaid quotations before payment can proceed.
          </p>
        </div>
      )}

      {/* ── Quotation cards ── */}
      <div className="space-y-3">
        {displayQuotations.map((quotation) => {
          const transport = getTransportStatus(quotation);
          const approval = getSampleApproval(quotation);
          const isApproved = quotation?.sampleDetails?.approval === "Approved";
          const isRejected = quotation?.sampleDetails?.approval === "Rejected";
          const alreadyProceeded = quotation?.buyerDecision === "Proceed_Full_Order";
          const isLoadingThisOne = fullOrderLoadingId === quotation._id;
          const sampleInvoiceConfirmed = !!quotation?.invoice_details?.sample?.proforma_invoice;
          const fullOrderInvoiceConfirmed = !!quotation?.invoice_details?.full_order?.proforma_invoice;
          const isPaid = isQuotationPaid(quotation._id);
          const isFullOrderCard = alreadyProceeded && quotation?.sampleDetails?.requested;
          const allBreakdowns = getAllBreakdowns(quotation);

          // All dispatch entries — sample and/or full order
          const allDispatches = Array.isArray(quotation?.dispatchDetails)
            ? quotation.dispatchDetails.filter((d) =>
              ["Sample", "Full_Order"].includes(d.type) &&
              ["Dispatched", "Delivered"].includes(d.status)
            )
            : [];

          return (
            <div
              key={quotation._id}
              className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${isFullOrderCard && fullOrderInvoiceConfirmed ? "border-blue-300 shadow-md shadow-blue-50"
                : isPaid ? "border-green-300 shadow-md shadow-green-50"
                  : isApproved && !alreadyProceeded ? "border-emerald-300 shadow-md shadow-emerald-50"
                    : isRejected ? "border-red-300 shadow-sm shadow-red-50 opacity-80"
                      : sampleInvoiceConfirmed ? "border-green-200"
                        : "border-gray-200 hover:border-gray-300"
                }`}
            >
              {isFullOrderCard && (() => {
                const fullDispatch = Array.isArray(quotation?.dispatchDetails)
                  ? quotation.dispatchDetails.find((d) => d.type === "Full_Order")
                  : null;
                const fullDelivered = fullDispatch?.status === "Delivered";
                const fullDispatched = fullDispatch?.status === "Dispatched";
                const fullOrderSatisfied = !!quotation?.fullOrderDetails?.approval;

                if (fullOrderSatisfied) {
                  return (
                    <div className="bg-emerald-600 px-4 py-1.5 flex items-center gap-2">
                      <FiCheck className="text-white text-xs" />
                      <span className="text-white text-[11px] font-bold tracking-wide">Full Order Complete — Thank You</span>
                    </div>
                  );
                }
                if (fullDelivered) {
                  return (
                    <div className="bg-purple-600 px-4 py-1.5 flex items-center gap-2">
                      <FiPackage className="text-white text-xs" />
                      <span className="text-white text-[11px] font-bold tracking-wide">Full Order Delivered — Awaiting Your Confirmation</span>
                    </div>
                  );
                }
                if (fullDispatched) {
                  return (
                    <div className="bg-blue-500 px-4 py-1.5 flex items-center gap-2">
                      <FiTruck className="text-white text-xs" />
                      <span className="text-white text-[11px] font-bold tracking-wide">Full Order Dispatched — On Its Way</span>
                    </div>
                  );
                }
                if (fullOrderInvoiceConfirmed) {
                  return (
                    <div className="bg-blue-400 px-4 py-1.5 flex items-center gap-2">
                      <FiCheck className="text-white text-xs" />
                      <span className="text-white text-[11px] font-bold tracking-wide">Full Order Paid — Preparing for Dispatch</span>
                    </div>
                  );
                }
                // Initiated but not yet paid
                return (
                  <div className="bg-orange-500 px-4 py-1.5 flex items-center gap-2">
                    <FiTruck className="text-white text-xs" />
                    <span className="text-white text-[11px] font-bold tracking-wide">Full Order Initiated — Payment Pending</span>
                  </div>
                );
              })()}
              {!isFullOrderCard && isPaid && (
                <div className="bg-green-500 px-4 py-1.5 flex items-center gap-2">
                  <FiCheck className="text-white text-xs" />
                  <span className="text-white text-[11px] font-bold tracking-wide">Sample Payment Completed</span>
                </div>
              )}
              {!isPaid && !isFullOrderCard && isApproved && (
                <div className="bg-emerald-500 px-4 py-1.5 flex items-center gap-2">
                  <FiCheck className="text-white text-xs" />
                  <span className="text-white text-[11px] font-bold tracking-wide">Sample Approved — Ready for Full Order</span>
                </div>
              )}
              {isRejected && (
                <div className="bg-gradient-to-r from-red-500 to-rose-600 px-4 py-2 flex items-center gap-2">
                  <FiX className="text-white text-xs flex-shrink-0" />
                  <span className="text-white text-[11px] font-bold tracking-wide">Sample Rejected</span>
                </div>
              )}

              <div className="p-4">
                {/* ── Top row: ID+date (left) | Sample approval (right) ── */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                      {isFullOrderCard ? "Sample → Full Order" : "Sample"} · Quotation
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      #{quotation.quotationUniqueNumber || quotation._id?.slice(-6)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {new Date(quotation.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-bold border flex-shrink-0 ${approval.color === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : approval.color === "red" ? "bg-red-50 text-red-700 border-red-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}>
                    <span>{approval.icon}</span>
                    <span>Sample approval: {approval.label}</span>
                  </div>
                </div>

                {/* ── Price Breakdowns — show all available (sample + full order) ── */}
                {allBreakdowns.length > 0 && (
                  <div className="mb-3 space-y-2.5">
                    {allBreakdowns.map((bd) => (
                      <div key={bd.type}>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full bg-green-400`} />
                          {bd.type === "full" ? "Full Order Pricing" : "Sample Pricing"}
                        </p>
                        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 sm:flex-wrap sm:overflow-visible">
                          {/* Base */}
                          <div className="flex-shrink-0 flex flex-col items-center bg-gray-50 border border-gray-100 rounded-xl px-3 py-2 min-w-[76px]">
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Base</p>
                            <p className="text-xs font-extrabold text-gray-800">₹{formatPrice(bd.data.baseAmount)}</p>
                            <p className="text-[9px] text-gray-400 mt-0.5">{bd.data.totalWeightKg} kg</p>
                          </div>
                          {/* GST */}
                          <div className="flex-shrink-0 flex flex-col items-center bg-gray-50 border border-gray-100 rounded-xl px-3 py-2 min-w-[68px]">
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-0.5">GST</p>
                            <p className="text-xs font-extrabold text-gray-800">₹{formatPrice(bd.data.gstAmount)}</p>
                            <p className="text-[9px] text-gray-400 mt-0.5">{bd.data.gstRate}%</p>
                          </div>
                          {/* Transport */}
                          <div className={`flex-shrink-0 flex flex-col items-center rounded-xl px-3 py-2 min-w-[76px] border ${bd.data.transportationCharges !== undefined && bd.data.transportationCharges !== null ? "bg-green-50 border-green-100" : "bg-amber-50 border-amber-100"
                            }`}>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Transport</p>
                            {bd.data.transportationCharges !== undefined && bd.data.transportationCharges !== null ? (
                              <p className="text-xs font-extrabold text-green-700">₹{formatPrice(bd.data.transportationCharges)}</p>
                            ) : (
                              <p className="text-xs font-extrabold text-amber-600">Pending</p>
                            )}
                            <p className="text-[9px] text-gray-400 mt-0.5">
                              {bd.type === "full" ? "Freight" : "Courier"}
                            </p>
                          </div>
                          {/* = */}
                          <div className="flex-shrink-0 flex items-center text-gray-300 font-bold text-lg self-center px-0.5">=</div>
                          {/* Total */}
                          <div className={`flex-shrink-0 flex flex-col items-center rounded-xl px-3 py-2 min-w-[76px] border bg-primaryColor/8 border-primaryColor/20`}>
                            <p className={`text-[9px] font-black uppercase tracking-wider mb-0.5 text-primaryColor/70`}>Total</p>
                            <p className={`text-xs font-extrabold text-primaryColor
                              }`}>₹{formatPrice(bd.data.totalAmount)}</p>
                            <p className="text-[9px] text-gray-400 mt-0.5">Incl. other charges</p>
                          </div>
                          {/* Pay via */}
                          {/* {bd.data.paymentDetails?.eligiblePaymentMethod && (
                            <div className={`flex-shrink-0 flex flex-col items-center rounded-xl px-3 py-2 min-w-[68px] border self-center ${bd.data.paymentDetails.eligiblePaymentMethod === "razorpay"
                              ? "bg-green-50 border-green-200"
                              : "bg-indigo-50 border-indigo-200"
                              }`}>
                              <p className="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Via</p>
                              <p className={`text-[10px] font-extrabold ${bd.data.paymentDetails.eligiblePaymentMethod === "razorpay"
                                ? "text-green-700" : "text-indigo-700"
                                }`}>
                                {bd.data.paymentDetails.eligiblePaymentMethod === "razorpay" ? "Razorpay" : "LC"}
                              </p>
                            </div>
                          )} */}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Dispatch timeline — show each dispatch entry ── */}
                {allDispatches.length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Dispatch Status</p>
                    {allDispatches.map((d) => {
                      const isDelivered = d.status === "Delivered";
                      const isFullType = d.type === "Full_Order";
                      return (
                        <div
                          key={d._id}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-xs font-semibold ${isDelivered
                            ? "bg-green-50 border-green-200 text-green-700"
                            : "bg-amber-50 border-amber-200 text-amber-700"
                            }`}
                        >
                          <span className="text-sm flex-shrink-0">
                            {isDelivered ? "✅" : "🚚"}
                          </span>
                          <div className="flex-1 min-w-0">
                            <span className="font-black">
                              {isFullType ? "Full Order" : "Sample"}:
                            </span>{" "}
                            {d.status}
                            {d.deliveredAt && isDelivered && (
                              <span className="ml-1 font-normal opacity-70">
                                · {new Date(d.deliveredAt).toLocaleDateString("en-GB", {
                                  day: "numeric", month: "short",
                                })}
                              </span>
                            )}
                          </div>
                          {/* Mini tracking dots */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {["Dispatched", "Out for Delivery", "Delivered"].map((step, i) => {
                              const stepDone =
                                step === "Dispatched" ? true
                                  : step === "Delivered" ? isDelivered
                                    : isDelivered;
                              return (
                                <span
                                  key={i}
                                  className={`w-1.5 h-1.5 rounded-full ${stepDone
                                    ? isDelivered
                                      ? "bg-green-500"
                                      : "bg-amber-400"
                                    : "bg-gray-200"
                                    }`}
                                />
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Status chips ── */}
                {/* <div className="flex flex-wrap gap-2 mb-3">
                  {isFullOrderCard && (
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border ${fullOrderInvoiceConfirmed
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                      <FiCreditCard className="text-[11px]" />
                      {fullOrderInvoiceConfirmed ? "Full Order Paid" : "Awaiting Full Order Payment"}
                    </div>
                  )}
                  {!isFullOrderCard && (
                    isPaid ? (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border bg-green-50 text-green-700 border-green-200">
                        <FiCheck className="text-[11px]" /> Sample Paid
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                        <FiClock className="text-[11px]" /> Payment Pending
                      </div>
                    )
                  )}
                </div> */}

                {/* ── Rejection reason ── */}
                {isRejected && (
                  <div className="mb-3 rounded-xl border border-red-100 bg-red-50/60 px-3.5 py-3">
                    <div className="flex items-start gap-2">
                      <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={13} />
                      <div className="min-w-0">
                        <p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Reason for Rejection</p>
                        {quotation?.sampleDetails?.remarks ? (
                          <p className="text-xs text-red-700 font-medium leading-relaxed">{quotation.sampleDetails.remarks}</p>
                        ) : (
                          <p className="text-xs text-red-400 italic">No reason provided.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Contextual status message ── */}
                {(() => {
                  const msg = getStatusMessage(quotation);
                  if (!msg) return null;
                  const styles = {
                    green: "bg-green-50 border-green-200 text-green-700",
                    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
                    amber: "bg-amber-50 border-amber-200 text-amber-700",
                    blue: "bg-blue-50 border-blue-200 text-blue-700",
                    primary: "bg-primaryColor/5 border-primaryColor/20 text-primaryColor",
                  };
                  const dotStyles = {
                    green: "bg-green-400", emerald: "bg-emerald-400 animate-pulse",
                    amber: "bg-amber-400 animate-pulse", blue: "bg-blue-400",
                    primary: "bg-primaryColor animate-pulse",
                  };
                  return (
                    <div className={`mb-3 rounded-xl border px-3.5 py-2.5 flex items-start gap-2.5 ${styles[msg.color]}`}>
                      <span className="text-sm flex-shrink-0 mt-0.5">{msg.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotStyles[msg.color]}`} />
                          <p className="text-[10px] font-black uppercase tracking-widest opacity-60">
                            {msg.type === "action" ? "Action Required"
                              : msg.type === "waiting" ? "Pending"
                                : msg.type === "success" ? "Confirmed" : "Info"}
                          </p>
                        </div>
                        <p className="text-xs font-semibold leading-relaxed">{msg.text}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* ── NEW: Bank transfer status (read-only) — actual upload is via the banner above ── */}
                {!isFullOrderCard &&
                  quotation?.paymentDetails?.sample?.method === "Direct_Bank_Transfer" &&
                  sampleInvoiceConfirmed && (
                    <div className="mb-3">
                      <SampleBankTransferStatusCard
                        bankTransferDocument={sampleBankTransferDocs[quotation._id] || null}
                      />
                    </div>
                  )}

                {/* ── Action buttons ── */}
                <div className="flex items-center gap-2 flex-wrap">
                  {!isRejected && (
                    <button
                      onClick={() => onViewManage(quotation)}
                      className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-white rounded-lg text-xs font-semibold bg-primaryBtnColor hover:bg-primaryBtnHoverColor hover:border-gray-300 transition-colors"
                    >
                      Click to View & Manage Quotation<FiArrowRight className="text-xs" />
                    </button>
                  )}
                  {sampleInvoiceConfirmed && (
                    <button
                      onClick={() => window.open(quotation.invoice_details.sample.proforma_invoice, "_blank")}
                      className="flex items-center gap-1.5 px-3 py-2 border border-green-200 text-green-700 rounded-xl text-xs font-semibold hover:bg-green-50 transition-colors"
                    >
                      View Sample Invoice →
                    </button>
                  )}
                  {fullOrderInvoiceConfirmed && (
                    <button
                      onClick={() => window.open(quotation.invoice_details.full_order.proforma_invoice, "_blank")}
                      className="flex items-center gap-1.5 px-3 py-2 border border-green-200 text-green-700 rounded-xl text-xs font-semibold hover:bg-green-50 transition-colors"
                    >
                      View Full Order Invoice →
                    </button>
                  )}
                  {isApproved && !alreadyProceeded && !isPaid && (
                    <button
                      onClick={() => onProceedFullOrder(quotation)}
                      disabled={updating}
                      className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primaryColor to-[#CC234F] text-white rounded-xl text-xs font-bold hover:opacity-90 transition-all shadow-md shadow-primaryColor/20 disabled:opacity-50 disabled:cursor-not-allowed flex-1 justify-center"
                    >
                      {isLoadingThisOne ? (
                        <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing...</>
                      ) : <>🚀 Proceed to Full Order</>}
                    </button>
                  )}
                  {alreadyProceeded && !fullOrderInvoiceConfirmed && (
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-orange-50 border border-orange-200 rounded-xl text-xs font-bold text-orange-700">
                      <FiCheck className="text-xs" /> Full Order Initiated
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SelectedQuotationsListView;